import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useVault } from "@/context/VaultContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { UnlockScreen } from "@/components/passord/UnlockScreen";
import { PasswordField } from "@/components/passord/PasswordField";
import { StrengthMeter } from "@/components/passord/StrengthMeter";
import { RecoveryCodeCard } from "@/components/passord/RecoveryCodeCard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { passwordStrength } from "@/lib/vaultCrypto";
import { errorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, ExternalLink, Timer, KeyRound, LifeBuoy, Trash2, Loader2, Radar } from "lucide-react";

export default function VaultSettingsPage() {
  const { user } = useAuth();
  const { status, meta, settings, updateSettings, changeMaster, regenerateRecovery, deleteVault, busy } = useVault();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current: "", next: "", repeat: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  if (status === "loading") return <Skeleton className="h-72" />;
  if (status === "none") return <EmptyState icon={ShieldCheck} title="Hvelvet er ikke satt opp" description="Sett opp hvelvet før du endrer innstillinger." actionLabel="Til hvelvet" actionTo="/passord" />;
  if (status === "locked") return <div className="space-y-6"><PageHeader eyebrow="Passord" title="Hvelvinnstillinger" /><UnlockScreen description="Lås opp for å endre hvelvinnstillinger." /></div>;

  const strength = passwordStrength(pw.next);
  const okNew = pw.next.length >= 12 && strength.score >= 2 && pw.next === pw.repeat;

  const saveSetting = async (patch) => {
    try { await updateSettings(patch); toast.success("Innstilling lagret"); } catch (e) { toast.error(errorMessage(e)); }
  };

  const submitMaster = async (e) => {
    e.preventDefault();
    if (!okNew) return;
    setPwBusy(true);
    try {
      await changeMaster(pw.current, pw.next);
      toast.success("Hovedpassordet er endret");
      setPw({ current: "", next: "", repeat: "" });
    } catch (err) {
      toast.error(err?.name === "OperationError" ? "Feil nåværende hovedpassord" : errorMessage(err));
    } finally {
      setPwBusy(false);
    }
  };

  const regen = async () => {
    setRecBusy(true);
    try { setRecovery(await regenerateRecovery()); } catch (err) { toast.error(errorMessage(err)); } finally { setRecBusy(false); }
  };

  const doDelete = async () => {
    setDelBusy(true);
    try {
      await deleteVault(delPw);
      toast.success("Hvelvet er slettet");
      setDelOpen(false);
      navigate("/passord");
    } catch (err) {
      toast.error(err?.name === "OperationError" ? "Feil hovedpassord" : errorMessage(err));
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Passord" title="Hvelvinnstillinger" description="Hurtigåpning, automatisk låsing, lekkasjesjekk, hovedpassord og gjenoppretting." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ExternalLink className="h-4 w-4 text-primary" /> Hurtigknappen «Gå til»</CardTitle><CardDescription>Hva skjer når du trykker «Gå til» på en oppføring.</CardDescription></CardHeader>
          <CardContent>
            <RadioGroup value={settings.quick_open_mode} onValueChange={(v) => saveSetting({ quick_open_mode: v })} className="space-y-2" data-testid="settings-quick-open">
              {[["open_only", "Åpne nettadressen", "Åpner systemet i ny fane. Kopier brukernavn og passord med egne knapper."], ["open_and_copy", "Åpne og kopier passordet", `Åpner i ny fane og legger passordet på utklippstavlen. Tømmes automatisk etter ${settings.clipboard_clear_seconds} sekunder.`]].map(([v, t, d]) => (
                <label key={v} className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer has-[[data-state=checked]]:border-primary">
                  <RadioGroupItem value={v} className="mt-0.5" data-testid={`settings-quick-open-${v}`} />
                  <span><span className="text-sm font-medium block">{t}</span><span className="text-xs text-muted-foreground">{d}</span></span>
                </label>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Timer className="h-4 w-4 text-primary" /> Automatisk låsing</CardTitle><CardDescription>Hvelvet låses etter inaktivitet, ved utlogging og når du lukker nettleseren.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Lås etter</Label>
              <Select value={String(settings.auto_lock_minutes)} onValueChange={(v) => saveSetting({ auto_lock_minutes: Number(v) })}>
                <SelectTrigger data-testid="settings-autolock-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{[1, 5, 10, 30].map((m) => <SelectItem key={m} value={String(m)}>{m} {m === 1 ? "minutt" : "minutter"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Hvelvnøkkelen holdes kun i minnet. Ved oppfriskning av siden må du låse opp igjen.</p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Radar className="h-4 w-4 text-primary" /> Lekkasjesjekk</CardTitle><CardDescription>Sjekker passordene anonymt mot kjente datalekkasjer (Have I Been Pwned) hver gang hvelvet låses opp, og varsler deg om et passord er på avveie.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-start justify-between gap-4 rounded-lg border p-3 cursor-pointer">
              <span>
                <span className="text-sm font-medium block">Sjekk passordene mot kjente lekkasjer</span>
                <span className="text-xs text-muted-foreground">Kjøres automatisk ved opplåsing og når du endrer et passord. Resultatet vises i Passordhelse og på oppføringen.</span>
              </span>
              <Switch checked={settings.breach_check_enabled !== false} onCheckedChange={(v) => saveSetting({ breach_check_enabled: v })} aria-label="Lekkasjesjekk" data-testid="settings-breach-check-switch" />
            </label>
            <p className="text-xs text-muted-foreground" data-testid="settings-breach-privacy">
              <strong className="text-foreground">Slik bevares nullkunnskapen:</strong> passordet hashes i nettleseren, og kun de 5 første tegnene av hashen sendes til Have I Been Pwned (k-anonymitet). Tjenesten svarer med flere hundre mulige treff, og sammenligningen skjer lokalt hos deg. Verken passord, full hash eller resultat sendes til Controlle-serveren.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Endre hovedpassord</CardTitle><CardDescription>Innholdet krypteres ikke om – bare nøkkelen pakkes inn på nytt. {meta?.master_updated_at ? `Sist endret ${formatDateTime(meta.master_updated_at)}.` : ""}</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={submitMaster} className="space-y-4" noValidate>
              <div className="space-y-1.5"><Label htmlFor="cm-current">Nåværende hovedpassord</Label><PasswordField id="cm-current" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} testId="settings-master-current" sensitive={false} autoComplete="current-password" /></div>
              <div className="space-y-1.5"><Label htmlFor="cm-next">Nytt hovedpassord</Label><PasswordField id="cm-next" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} testId="settings-master-next" sensitive={false} /><StrengthMeter password={pw.next} testId="settings-master-strength" /></div>
              <div className="space-y-1.5"><Label htmlFor="cm-repeat">Gjenta nytt hovedpassord</Label><PasswordField id="cm-repeat" value={pw.repeat} onChange={(e) => setPw((p) => ({ ...p, repeat: e.target.value }))} testId="settings-master-repeat" sensitive={false} />{pw.repeat && pw.repeat !== pw.next ? <p className="text-xs text-destructive">Passordene er ikke like.</p> : null}</div>
              <Button type="submit" disabled={pwBusy || !pw.current || !okNew} data-testid="settings-master-submit">{pwBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Endre hovedpassord</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-primary" /> Gjenopprettingskode</CardTitle><CardDescription>{meta?.has_recovery ? `Kode registrert${meta.recovery_updated_at ? ` ${formatDateTime(meta.recovery_updated_at)}` : ""}. Lag en ny hvis du har mistet den – den gamle slutter da å virke.` : "Ingen gjenopprettingskode er registrert. Lag en nå."}</CardDescription></CardHeader>
          <CardContent>
            {recovery ? (
              <RecoveryCodeCard code={recovery} userEmail={user?.email} confirmLabel="Jeg har lagret den nye koden" onConfirmed={() => { setRecovery(null); toast.success("Ny gjenopprettingskode er aktiv"); }} />
            ) : (
              <Button variant="outline" onClick={regen} disabled={recBusy || busy} data-testid="settings-recovery-regenerate">{recBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LifeBuoy className="h-4 w-4 mr-2" />} {meta?.has_recovery ? "Lag ny gjenopprettingskode" : "Lag gjenopprettingskode"}</Button>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-destructive/30">
          <CardHeader><CardTitle className="text-base flex items-center gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Slett hvelvet</CardTitle><CardDescription>Sletter alle passord og delinger permanent. Kan ikke angres.</CardDescription></CardHeader>
          <CardContent><Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDelOpen(true)} data-testid="settings-delete-vault-button">Slett hvelvet …</Button></CardContent>
        </Card>
      </div>

      <Dialog open={delOpen} onOpenChange={(o) => { setDelOpen(o); if (!o) setDelPw(""); }}>
        <DialogContent className="bg-card sm:max-w-md" data-testid="settings-delete-dialog">
          <DialogHeader><DialogTitle>Slette hele hvelvet?</DialogTitle><DialogDescription>Skriv hovedpassordet for å bekrefte. Alle oppføringer og delinger slettes for godt.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 py-2"><Label htmlFor="del-pw">Hovedpassord</Label><PasswordField id="del-pw" value={delPw} onChange={(e) => setDelPw(e.target.value)} testId="settings-delete-master" sensitive={false} autoComplete="current-password" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} data-testid="settings-delete-cancel">Avbryt</Button>
            <Button variant="destructive" onClick={doDelete} disabled={delBusy || !delPw} data-testid="settings-delete-confirm">{delBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />} Slett hvelvet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
